import { FiltroDeTitulos, Titulo } from "@/models/financeiro/Titulo";
import { formatarMoeda } from "./formatadores";
import Decimal from 'decimal.js';

export function limparFiltroDosCamposDeTitulos(): FiltroDeTitulos {
	return {
		pessoa: '',
		tipoDeCliente: '',
		dataInicioPeriodo: '',
		dataFimPeriodo: '',
		formaDePagamento: '',
		numeroDocumento: '',
		dataInicioPagamento: '',
		dataFimPagamento: '',
		somenteComValorDesatualizado: false,
		somenteComErroDeRecalculo: false,
	}
}

export function calcularDataAposCarencia(titulo: Titulo, dataVencimento: Date): Date {
	const diasDeCarencia = titulo.diasDeCarencia || 0;

	const dataCarencia = new Date(
		dataVencimento.getFullYear(),
		dataVencimento.getMonth(),
		dataVencimento.getDate(),
	);

	if (diasDeCarencia > 0) {
		dataCarencia.setDate(dataCarencia.getDate() + diasDeCarencia);
	} else {
		const diaSemanaVencimento = dataCarencia.getDay();
		if (diaSemanaVencimento === 6) {
			dataCarencia.setDate(dataCarencia.getDate() + 2);
		} else if (diaSemanaVencimento === 0) {
			dataCarencia.setDate(dataCarencia.getDate() + 1);
		}
	}

	return new Date(
		dataCarencia.getFullYear(),
		dataCarencia.getMonth(),
		dataCarencia.getDate(),
	);
}


/**
 * Diz se o título ainda tem saldo a receber, ou seja, se ele pode entrar num pagamento.
 *
 * Este é o predicado ÚNICO da regra: ele decide quem aparece na grid do pagamento múltiplo,
 * quem pode nascer pré-selecionado e quem sobrevive quando a seleção é reconciliada depois
 * de uma busca. As três coisas precisam usar o mesmo critério, senão um título quitado some
 * da tela mas continua selecionado por baixo, soma no total e acaba recebendo uma baixa de
 * R$ 0,00.
 *
 * O saldo é o critério, e não a situação do título: existe título marcado como pago que
 * ainda tem saldo em aberto.
 *
 * Recebe o título BRUTO, nunca o formatado para exibição, cujos valores já são string.
 */
export function tituloTemSaldoAPagar(titulo: { valorAtualizado?: any; valor?: any }): boolean {
	return Number(titulo.valorAtualizado ?? titulo.valor) > 0
}

/**
 * Multa devida por um título vencido cujo campo de multa está zerado.
 *
 * Esse campo quase nunca é populado (448 de 452 títulos abertos e vencidos estavam com ele
 * zerado na base de dev), então a multa de título vencido é materializada aqui, a partir do
 * percentual configurado, e não lida do título.
 *
 * O que o campo zerado NÃO diz é o motivo de estar zerado. Ele fica assim tanto num título
 * que nunca teve multa lançada quanto num em que a multa foi PERDOADA por desconto numa
 * baixa anterior. Recriar a multa cheia no segundo caso cobra de novo o que já foi perdoado,
 * que era o defeito relatado. Por isso o valor perdoado entra como parâmetro e é abatido.
 *
 * O abatimento é feito aqui, sobre o valor RECRIADO, e nunca depois, sobre um valor que veio
 * do backend: aquele já chega líquido, e abater de novo seria subtrair duas vezes.
 *
 * Estorno não é afetado. Como o estorno é lançamento compensatório negativo e não exclusão,
 * a soma do perdoado volta a zero sozinha e a multa continua sendo recriada por inteiro.
 */
export function calcularMultaDeTituloVencido(
	porcentagemDeMulta: number,
	valorTitulo: number,
	multaJaPerdoada = 0,
): number {
	if (!porcentagemDeMulta || !valorTitulo) return 0

	const multaCheia = Number(
		(Number(porcentagemDeMulta) / 100 * Number(valorTitulo)).toFixed(2),
	)

	return Math.max(0, Number((multaCheia - Number(multaJaPerdoada || 0)).toFixed(2)))
}


/**
 * Soma o saldo que os títulos marcados no Pagamento Múltiplo ainda devem.
 *
 * Cada item da seleção carrega o saldo já formatado para exibição ("R$ 1.234,56"), recalculado
 * para a data de pagamento escolhida. Esta soma é o teto do pagamento, então o desmonte da
 * máscara precisa ser exato: perder o separador de milhar aqui alarga a trava em mil vezes.
 */
export function somarSaldoDosTitulosSelecionados(
	titulosSelecionados: { valorAtualizado?: any }[],
): number {
	const soma = titulosSelecionados.reduce((total, titulo) => {
		const saldo = titulo.valorAtualizado
		// O ramo numérico não é firula: passar um number pelo desmonte da máscara faria o regex
		// comer o ponto decimal e 59.2 viraria 592, alargando o teto em dez vezes sem erro nenhum.
		const valor = typeof saldo === 'number'
			? saldo
			: parseFloat(String(saldo).replace(/[^\d,-]/g, '').replace(',', '.'))
		return total + (isNaN(valor) ? 0 : valor)
	}, 0)

	return Number.parseFloat(soma.toFixed(2))
}


/**
 * Juros que o Pagamento Múltiplo envia para a baixa quando o Saldo Devedor exibido é o do backend.
 *
 * O backend não recalcula o juros na hora da baixa: ele usa o que a tela manda, abate a multa,
 * o juros e por último o principal. Se a tela exibe o saldo do backend mas envia o juros da
 * própria conta, a soma enviada fica menor que o valor pago e a sobra é descartada. Foi o 42375:
 * com "juros sobre multa" ligado e baixa parcial anterior, o backend põe a multa já paga na base
 * do juros e a tela não, e R$ 0,08 sobraram do pagamento e voltaram como juros em aberto.
 *
 * A diferença vai inteira para o juros porque é só nele que as duas contas divergem: a multa e
 * o principal restante saem iguais nas duas.
 */
export function calcularJurosParaFecharComSaldoDoBackend(
	saldoDoBackend: number,
	principalRestante: number | Decimal,
	multa: number,
): number {
	return Math.max(0, new Decimal(saldoDoBackend || 0)
		.sub(principalRestante || 0)
		.sub(multa || 0)
		.toDecimalPlaces(2)
		.toNumber())
}


/**
 * Valida o campo "Valor a Pagar" do Pagamento Múltiplo contra o saldo dos títulos selecionados.
 *
 * O teto é o saldo NA DATA DE PAGAMENTO informada, e não o saldo de hoje. Numa baixa retroativa
 * os encargos do período que ainda não passou deixam de existir, o saldo cai, e o backend só
 * consegue baixar até esse saldo menor: ele distribui o valor em cascata por vencimento, cada
 * título consome até o próprio teto e o que sobra é descartado sem erro nenhum. Foi o que gerou
 * o 39094, em que R$ 64,25 informados viraram R$ 59,20 no comprovante.
 *
 * O valor chega aqui como a STRING formatada em pt-BR do CampoDecimal ("1.234,56"), e não como
 * o número do v-model: quem alimenta as :rules é o v-text-field interno do componente. O ramo
 * numérico existe só para quem chamar isto direto.
 */
export function validarValorDoPagamentoEmMassa(
	valorDigitado: string | number | null | undefined,
	saldoDosTitulos: number,
): true | string {
	if (valorDigitado === null || valorDigitado === undefined || valorDigitado === '') {
		return 'Valor de pagamento é obrigatório'
	}

	const valor = typeof valorDigitado === 'number'
		? valorDigitado
		: parseFloat(valorDigitado.replace(/\./g, '').replace(',', '.'))

	if (isNaN(valor)) return 'Valor de pagamento é obrigatório'

	if (valor > saldoDosTitulos) {
		return `Valor superior ao saldo dos títulos na data (R$ ${formatarMoeda(saldoDosTitulos)})`
	}

	return true
}
